/**
 * Technician Type Title Resolution
 * 
 * Strict precedence: shop-set ("Confirmed by Shop") > inferred ("Inferred by OptiBay") > Self-Reported > "Technician"
 */

import type { ProficiencyData } from '@/utils/technicianPassportUtils';
import { isMechanicalProficiencyCategory } from '@/constants/proficiencyCategories';

// ─── Constants ───────────────────────────────────────────────────────

export type TechnicianTypeKey =
  | 'student'
  | 'apprentice'
  | 'tire_lube'
  | 'general_service'
  | 'general_tech'
  | 'diagnostician'
  | 'master_tech';

export type TechnicianTypeSource = 'shop_verified' | 'inferred' | 'self' | 'none';

export const TECHNICIAN_TYPE_LABELS: Record<TechnicianTypeKey, string> = {
  student: 'Student',
  apprentice: 'Apprentice',
  tire_lube: 'Tire/Lube Technician',
  general_service: 'General Service Technician',
  general_tech: 'General Technician',
  diagnostician: 'Diagnostician',
  master_tech: 'Master Technician',
};

/** Self-report dropdown options (excludes master_tech — inference/shop-verify only) */
export const TECHNICIAN_TYPE_OPTIONS: { value: TechnicianTypeKey; label: string }[] = [
  { value: 'student', label: 'Student' },
  { value: 'apprentice', label: 'Apprentice' },
  { value: 'tire_lube', label: 'Tire/Lube Technician' },
  { value: 'general_service', label: 'General Service Technician' },
  { value: 'general_tech', label: 'General Technician' },
  { value: 'diagnostician', label: 'Diagnostician' },
];

/** All options including master_tech (for shop-verified dropdown) */
export const TECHNICIAN_TYPE_ALL_OPTIONS: { value: TechnicianTypeKey; label: string }[] = [
  ...TECHNICIAN_TYPE_OPTIONS,
  { value: 'master_tech', label: 'Master Technician' },
];

// ─── Source badge display ────────────────────────────────────────────

// OD-35 (2026-10-15): a shop-set type is the shop's confirmation (nobody verified evidence), and an inferred type is
// OptiBay's calculation. "Verified" is never shown without a named verifier.
export const SOURCE_BADGE_CONFIG: Record<TechnicianTypeSource, { label: string; className: string }> = {
  shop_verified: { label: 'Confirmed by Shop', className: 'bg-success/10 text-success border-success/30' },
  inferred: { label: 'Inferred by OptiBay', className: 'bg-primary/10 text-primary border-primary/30' },
  self: { label: 'Self-Reported', className: 'bg-muted text-muted-foreground border-border' },
  none: { label: '', className: '' },
};

// ─── Proficiency signal check ────────────────────────────────────────

/**
 * CORE-C GAP-007. This gate is load-bearing beyond display: a true result makes
 * the title `inferred`, which both Passport pages then PERSIST to
 * `technician_profiles` (`technician_type_source`), and that stored value now
 * carries a visible provenance badge. So unknown metadata must fail it.
 *
 * The semantics are unchanged — the thresholds are the owner's — but "unknown"
 * is now excluded EXPLICITLY rather than by relying on `null || 0` happening to
 * fall below them. A score existing is not evidence of how much work is behind
 * it, and a Passport must not acquire a durable inferred identity on that basis.
 */
export function hasSufficientProficiencySignal(proficiencies: ProficiencyData[]): boolean {
  // The technician TYPE is a mechanical title: detail work is real proficiency
  // but no evidence for it (see NON_MECHANICAL_PROFICIENCY_CATEGORIES).
  const withKnownSample = mechanicalOnly(proficiencies).filter((p) => p.sample_size !== null);

  const totalSamples = withKnownSample.reduce((sum, p) => sum + (p.sample_size as number), 0);
  if (totalSamples >= 5) return true;

  return withKnownSample.some(
    (p) =>
      (p.sample_size as number) >= 3 &&
      p.confidence_score !== null &&
      p.confidence_score >= 0.5,
  );
}

// ─── Inference algorithm ─────────────────────────────────────────────

/** High-complexity categories used for master_tech / diagnostician checks */
const HIGH_COMPLEXITY_CATEGORIES = [
  'Electrical',
  'Diagnostics',
  'EV / Hybrid High Voltage',
  'ADAS',
  'Transmission',
  'Engine Mechanical',
];

const HIGH_SKILL_CATEGORIES = [
  'Diagnostics',
  'Electrical',
  'Engine Mechanical',
  'Transmission',
];

function getProf(proficiencies: ProficiencyData[], key: string) {
  return proficiencies.find((p) => p.category_key === key);
}

function isStrong(p: ProficiencyData | undefined, minScore = 70, minConf = 0.6, minSample = 3): boolean {
  if (!p) return false;
  return p.proficiency_score >= minScore && p.confidence_score >= minConf && (p.sample_size || 0) >= minSample;
}

/** A mechanical title is inferred from mechanical proficiency only. */
function mechanicalOnly(proficiencies: ProficiencyData[]): ProficiencyData[] {
  return proficiencies.filter((p) => isMechanicalProficiencyCategory(p.category_key));
}

export function inferTechnicianTypeFromProficiencies(allProficiencies: ProficiencyData[]): TechnicianTypeKey {
  const proficiencies = mechanicalOnly(allProficiencies);
  // A) Master Tech: 4+ strong categories, at least one high-complexity
  const strongCategories = proficiencies.filter((p) => isStrong(p));
  const hasHighComplexityStrong = strongCategories.some((p) =>
    HIGH_COMPLEXITY_CATEGORIES.includes(p.category_key),
  );
  if (strongCategories.length >= 4 && hasHighComplexityStrong) {
    return 'master_tech';
  }

  // B) Diagnostician
  const diag = getProf(proficiencies, 'Diagnostics');
  if (
    diag &&
    diag.proficiency_score >= 70 &&
    (diag.sample_size || 0) >= 3 &&
    diag.confidence_score >= 0.5
  ) {
    const elec = getProf(proficiencies, 'Electrical');
    const engPerf = getProf(proficiencies, 'Engine Performance');
    if (
      (elec && elec.proficiency_score >= 65 && (elec.sample_size || 0) >= 2 && elec.confidence_score >= 0.4) ||
      (engPerf && engPerf.proficiency_score >= 65 && (engPerf.sample_size || 0) >= 2 && engPerf.confidence_score >= 0.4)
    ) {
      return 'diagnostician';
    }
  }

  // C) Tire & Lube
  const maint = getProf(proficiencies, 'Maintenance');
  const tires = getProf(proficiencies, 'Tires');
  const maintStrong = maint && maint.proficiency_score >= 70 && (maint.sample_size || 0) >= 2 && maint.confidence_score >= 0.4;
  const tiresStrong = tires && tires.proficiency_score >= 70 && (tires.sample_size || 0) >= 2 && tires.confidence_score >= 0.4;

  if (maintStrong || tiresStrong) {
    const highSkillAbsent = HIGH_SKILL_CATEGORIES.every((cat) => {
      const p = getProf(proficiencies, cat);
      return !p || p.proficiency_score < 55 || (p.sample_size || 0) < 2;
    });
    if (highSkillAbsent) {
      return 'tire_lube';
    }
  }

  // D) General Service
  const brakes = getProf(proficiencies, 'Brakes');
  const steer = getProf(proficiencies, 'Steering & Suspension');
  const align = getProf(proficiencies, 'Alignment');
  const serviceStrong = [brakes, steer, align].some(
    (p) => p && p.proficiency_score >= 60 && (p.sample_size || 0) >= 2 && p.confidence_score >= 0.4,
  );
  if (serviceStrong) {
    const diagWeak = !diag || diag.proficiency_score < 65 || (diag.sample_size || 0) < 2;
    if (diagWeak) {
      return 'general_service';
    }
  }

  // E) Default
  return 'general_tech';
}

// ─── Title resolution ────────────────────────────────────────────────

export interface TechnicianProfileForTitle {
  technician_type_shop_verified?: string | null;
  technician_type_inferred?: string | null;
  technician_type_source?: string | null;
}

export interface PassportProfileForTitle {
  technician_type_self_reported?: string | null;
}

export interface ResolvedTechnicianTitle {
  /** The raw key (e.g. 'diagnostician') or null for fallback */
  typeKey: TechnicianTypeKey | null;
  /** Human-readable display title */
  displayTitle: string;
  /** Where the title came from */
  source: TechnicianTypeSource;
  /** Whether the user can edit the title */
  isLocked: boolean;
  /** If inference was computed, the inferred key (for persistence comparison) */
  inferredTypeKey?: TechnicianTypeKey;
}

export function resolveTechnicianTypeTitle(
  technicianProfile?: TechnicianProfileForTitle | null,
  passportProfile?: PassportProfileForTitle | null,
  proficiencies: ProficiencyData[] = [],
): ResolvedTechnicianTitle {
  // 1. Shop-verified (always wins)
  if (
    technicianProfile?.technician_type_source === 'shop_verified' &&
    technicianProfile?.technician_type_shop_verified
  ) {
    const key = technicianProfile.technician_type_shop_verified as TechnicianTypeKey;
    return {
      typeKey: key,
      displayTitle: TECHNICIAN_TYPE_LABELS[key] || key,
      source: 'shop_verified',
      isLocked: true,
    };
  }

  // 2. Inferred from proficiency signal
  const hasSignal = hasSufficientProficiencySignal(proficiencies);
  if (hasSignal) {
    const inferredKey = inferTechnicianTypeFromProficiencies(proficiencies);
    return {
      typeKey: inferredKey,
      displayTitle: TECHNICIAN_TYPE_LABELS[inferredKey],
      source: 'inferred',
      isLocked: true,
      inferredTypeKey: inferredKey,
    };
  }

  // 3. Self-reported
  if (passportProfile?.technician_type_self_reported) {
    const key = passportProfile.technician_type_self_reported as TechnicianTypeKey;
    return {
      typeKey: key,
      displayTitle: TECHNICIAN_TYPE_LABELS[key] || key,
      source: 'self',
      isLocked: false,
    };
  }

  // 4. Fallback
  return {
    typeKey: null,
    displayTitle: 'Technician',
    source: 'none',
    isLocked: false,
  };
}
